import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Dumbbell, Clock, Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";

interface Exercise {
  id: string;
  name: string;
  sets: number | null;
  reps: number | null;
  weight_kg: number | null;
  distance_km: number | null;
  duration_seconds: number | null;
  notes: string | null;
  muscle_groups: string[];
  equipment: string | null;
  created_at?: string;
  set_details?: any;
}

interface Workout {
  id: string;
  title: string;
  description: string | null;
  workout_type: string | null;
  duration_minutes: number | null;
  calories_burned: number | null;
  completed_at: string;
  notes: string | null;
  exercises: Exercise[];
}

interface WorkoutImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkoutSelect: (formattedWorkout: string, workoutData?: any) => void;
}

export const WorkoutImportModal = ({ isOpen, onClose, onWorkoutSelect }: WorkoutImportModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadWorkouts();
    }
  }, [isOpen, user]);

  const loadWorkouts = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('workouts')
        .select(`
          *,
          exercises(*)
        `)
        .eq('user_id', user.id)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setWorkouts(data || []);
    } catch (error) {
      console.error('Error loading workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Strip every key that could leak raw GPS or other huge arrays from a notes
  // JSON blob. We never want to send these to the AI.
  const SAFE_RUN_NOTE_KEYS = new Set([
    'pace',
    'avgSpeedKmh',
    'averageSpeedKmh',
    'feeling',
    'pauseCount',
    'estimatedCalories',
    'difficulty',
    'userNotes',
    'note',
  ]);

  // Best-effort: pull a clean human-readable note out of an exercise/workout
  // notes string. If the string is JSON, only return the `userNotes`/`note`
  // field. Otherwise return the trimmed string capped at 300 chars. Returns
  // null when there is nothing safe to surface.
  const safeNoteText = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const obj = JSON.parse(trimmed);
        const candidate =
          (typeof obj?.userNotes === 'string' && obj.userNotes.trim()) ||
          (typeof obj?.note === 'string' && obj.note.trim()) ||
          '';
        return candidate ? candidate.slice(0, 300) : null;
      } catch {
        return null;
      }
    }
    return trimmed.slice(0, 300);
  };

  // Detect running workouts robustly. Old rows may have workout_type as null,
  // 'cardio', or use a different exercise name like 'Run' / 'Outdoor Run'.
  const detectRunningExercise = (workout: Workout): Exercise | null => {
    const exs = workout.exercises || [];
    const byName = exs.find(e => /run/i.test(e.name || ''));
    if (byName) return byName;
    const byDistance = exs.find(e => typeof e.distance_km === 'number' && e.distance_km > 0);
    if (byDistance) return byDistance;
    if (workout.workout_type === 'running' && exs[0]) return exs[0];
    return null;
  };

  const formatWorkout = (workout: Workout): string => {
    if (!workout.completed_at) return '';

    const date = new Date(workout.completed_at).toLocaleDateString();
    const time = new Date(workout.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const runningExercise = detectRunningExercise(workout);
    const isRunningWorkout = !!runningExercise || workout.workout_type === 'running';

    let formatted = `**${workout.title}**\n`;
    formatted += `${date} at ${time}\n`;
    if (workout.duration_minutes) {
      formatted += `Duration: ${workout.duration_minutes} minutes\n`;
    }
    if (workout.workout_type) {
      formatted += `Type: ${workout.workout_type}\n`;
    }
    const workoutLevelNote = safeNoteText(workout.notes);
    if (workoutLevelNote) {
      formatted += `Notes: ${workoutLevelNote}\n`;
    }

    formatted += `\n**Detailed Exercises:**\n`;

    // Running formatting mirrors the post-run analysis prompt: structured
    // summary only, never raw GPS / routePoints.
    if (isRunningWorkout) {
      if (runningExercise) {
        formatted += `**Running Session**\n`;
        if (runningExercise.distance_km) {
          formatted += `   • Distance: ${runningExercise.distance_km} km\n`;
        }
        if (runningExercise.duration_seconds) {
          const mins = Math.floor(runningExercise.duration_seconds / 60);
          const secs = runningExercise.duration_seconds % 60;
          formatted += `   • Duration: ${mins}:${String(secs).padStart(2, '0')}\n`;
        }
        if (runningExercise.notes) {
          try {
            const runData = JSON.parse(runningExercise.notes);
            if (runData.pace) formatted += `   • Avg Pace: ${runData.pace}/km\n`;
            const avgSpeed = runData.avgSpeedKmh ?? runData.averageSpeedKmh;
            if (avgSpeed) formatted += `   • Avg Speed: ${avgSpeed} km/h\n`;
            if (runData.feeling) formatted += `   • Feeling: ${runData.feeling}\n`;
            if (typeof runData.pauseCount === 'number' && runData.pauseCount > 0) {
              formatted += `   • Pauses taken: ${runData.pauseCount}\n`;
            }
            if (runData.splits && Array.isArray(runData.splits) && runData.splits.length > 0) {
              const splitText = runData.splits
                .map((s: any) => {
                  if (typeof s?.km !== 'undefined' && s?.pace) return `${s.km}km @ ${s.pace}/km`;
                  if (typeof s?.kilometer !== 'undefined' && s?.time) return `${s.kilometer}km @ ${s.time}`;
                  return null;
                })
                .filter(Boolean)
                .join(', ');
              if (splitText) formatted += `   • Splits: ${splitText}\n`;
            }
            const userRunNote = safeNoteText(runData.userNotes || runData.note);
            if (userRunNote) formatted += `   • Notes: ${userRunNote}\n`;
            // routePoints / location_points / coords are intentionally NEVER
            // included - raw GPS would balloon the prompt by tens of KB.
          } catch {
            // Notes wasn't JSON. Show a tiny preview only, never the full blob.
            const preview = runningExercise.notes.trim().slice(0, 200);
            if (preview) formatted += `   • Details: ${preview}${runningExercise.notes.length > 200 ? '...' : ''}\n`;
          }
        }
        if (workout.calories_burned) {
          formatted += `   • Calories: ~${workout.calories_burned} kcal\n`;
        }
      }
    } else {
      // Strength workouts: normalize legacy rows (one row per set) and new format (set_details)
      const byName = new Map<string, Exercise[]>();
      for (const ex of workout.exercises || []) {
        const key = (ex.name || '').trim();
        if (!key) continue;
        byName.set(key, [...(byName.get(key) || []), ex]);
      }

      const grouped = Array.from(byName.entries());

      grouped.forEach(([exerciseName, rows], index) => {
        formatted += `${index + 1}. **${exerciseName}**\n`;

        // Prefer the row that contains set_details (new format). If multiple, take the longest.
        const bestWithDetails = rows
          .filter(r => Array.isArray((r as any).set_details) && ((r as any).set_details?.length || 0) > 0)
          .sort((a, b) => (((b as any).set_details?.length || 0) - ((a as any).set_details?.length || 0)))[0];

        type SetLine = { set_number: number; reps: number | null; weight_kg: number | null; drop_set?: boolean };
        let sets: SetLine[] = [];

        if (bestWithDetails) {
          const raw = ((bestWithDetails as any).set_details as any[])
            .filter(s => s && (s.completed ?? true));

          sets = raw
            .map((s, idx) => ({
              set_number: typeof s.set_number === 'number' ? s.set_number : idx + 1,
              reps: typeof s.reps === 'number' ? s.reps : null,
              weight_kg: typeof (s.weight_kg ?? s.weight) === 'number' ? (s.weight_kg ?? s.weight) : null,
              drop_set: !!(s.drop_set ?? s.isDropSet),
            }))
            .sort((a, b) => a.set_number - b.set_number);
        } else {
          // Legacy:
          // - WorkoutLogger style: 1 row per exercise with `sets` as total sets.
          // - Older GymWorkout style: multiple rows per exercise, each row = one set (often `sets` is the set number).
          if (rows.length === 1) {
            const r = rows[0];
            if (
              typeof r.sets === "number" &&
              r.sets > 1 &&
              typeof r.reps === "number" &&
              typeof r.weight_kg === "number"
            ) {
              sets = Array.from({ length: r.sets }, (_, i) => ({
                set_number: i + 1,
                reps: r.reps,
                weight_kg: r.weight_kg,
                drop_set: false,
              }));
            } else {
              sets = [
                {
                  set_number: 1,
                  reps: typeof r.reps === "number" ? r.reps : null,
                  weight_kg: typeof r.weight_kg === "number" ? r.weight_kg : null,
                  drop_set: false,
                },
              ];
            }
          } else {
            sets = rows
              .map((r, idx) => ({
                set_number: typeof r.sets === "number" && r.sets > 0 ? r.sets : idx + 1,
                reps: typeof r.reps === "number" ? r.reps : null,
                weight_kg: typeof r.weight_kg === "number" ? r.weight_kg : null,
                drop_set: false,
              }))
              .sort((a, b) => a.set_number - b.set_number);
          }
        }

        if (sets.length > 0) {
          const first = sets[0];
          const allSame =
            first.reps !== null &&
            first.weight_kg !== null &&
            sets.every(s => s.reps === first.reps && s.weight_kg === first.weight_kg);

          if (allSame) {
            formatted += `   • ${sets.length} sets × ${first.reps} reps @ ${first.weight_kg}kg\n`;
          } else {
            formatted += `   • ${sets.length} sets\n`;
          }

          sets.forEach((s, i) => {
            const repsText = s.reps !== null ? `${s.reps} reps` : `? reps`;
            const weightText = s.weight_kg !== null ? `${s.weight_kg}kg` : `?kg`;
            formatted += `     - Set ${i + 1}: ${repsText} × ${weightText}${s.drop_set ? ' [Drop set]' : ''}\n`;
          });
        } else {
          formatted += `   • No completed sets\n`;
        }

        const meta = bestWithDetails || rows[0];
        if (meta?.muscle_groups && meta.muscle_groups.length > 0) {
          formatted += `   • Muscle groups: ${meta.muscle_groups.join(', ')}\n`;
        }
        if (meta?.equipment) {
          formatted += `   • Equipment: ${meta.equipment}\n`;
        }
        const exerciseNote = safeNoteText(meta?.notes);
        if (exerciseNote) {
          formatted += `   • Notes: ${exerciseNote}\n`;
        }
        formatted += `\n`;
      });
    }

    return formatted;
  };

  const buildWorkoutAnalysisPayload = (workout: Workout) => {
    const runningExercise = detectRunningExercise(workout);
    const isRunningWorkout = !!runningExercise || workout.workout_type === 'running';
    const workoutLevelNote = safeNoteText(workout.notes);

    if (isRunningWorkout && runningExercise) {
      let runData: any = {};
      if (runningExercise.notes) {
        try {
          runData = JSON.parse(runningExercise.notes);
        } catch {
          runData = {};
        }
      }
      return {
        type: 'running',
        title: workout.title,
        duration:
          workout.duration_minutes ??
          (runningExercise.duration_seconds ? Math.round(runningExercise.duration_seconds / 60) : 0),
        distance: runningExercise.distance_km ?? undefined,
        pace: runData.pace,
        averageSpeedKmh: runData.avgSpeedKmh ?? runData.averageSpeedKmh,
        estimatedCalories: workout.calories_burned ?? runData.estimatedCalories,
        feeling: runData.feeling,
        pauseCount: runData.pauseCount,
        splits: Array.isArray(runData.splits) ? runData.splits : undefined,
        notes: safeNoteText(runData.userNotes || runData.note) ?? workoutLevelNote ?? undefined,
      };
    }

    const strengthRows = (workout.exercises || []).filter((ex) => {
      if (ex.distance_km && ex.distance_km > 0) return false;
      const details = Array.isArray(ex.set_details) ? ex.set_details : [];
      return details.length > 0 || ex.sets || ex.reps || ex.weight_kg;
    });

    if (strengthRows.length > 0 && workout.workout_type?.toLowerCase() !== 'custom') {
      const byName = new Map<string, Exercise[]>();
      for (const ex of strengthRows) {
        const key = (ex.name || '').trim();
        if (!key) continue;
        byName.set(key, [...(byName.get(key) || []), ex]);
      }

      const exercises = Array.from(byName.entries()).map(([exerciseName, rows], index) => {
        const bestWithDetails = rows
          .filter(r => Array.isArray((r as any).set_details) && ((r as any).set_details?.length || 0) > 0)
          .sort((a, b) => (((b as any).set_details?.length || 0) - ((a as any).set_details?.length || 0)))[0];

        const rawSets = bestWithDetails
          ? ((bestWithDetails as any).set_details as any[]).filter(s => s && (s.completed ?? true))
          : rows.map((r, idx) => ({
              set_number: typeof r.sets === 'number' && r.sets > 0 ? r.sets : idx + 1,
              reps: r.reps,
              weight_kg: r.weight_kg,
              completed: true,
            }));

        const sortedSets = rawSets
          .map((s, idx) => ({
            reps: typeof s.reps === 'number' ? s.reps : null,
            weight: typeof (s.weight_kg ?? s.weight) === 'number' ? (s.weight_kg ?? s.weight) : null,
            restSecondsTaken: typeof s.rest_seconds_taken === 'number' ? s.rest_seconds_taken : null,
            isDropSet: !!(s.drop_set ?? s.isDropSet),
            isWeightPB: !!s.isWeightPB,
            isRepsPB: !!s.isRepsPB,
            isTotalWeightPB: !!(s.isTotalWeightPB ?? s.isVolumePB),
            setNumber: typeof s.set_number === 'number' ? s.set_number : idx + 1,
          }))
          .sort((a, b) => a.setNumber - b.setNumber);

        const meta = bestWithDetails || rows[0];
        const exerciseNote = safeNoteText(meta?.notes);
        return {
          exerciseOrder: index + 1,
          name: exerciseName,
          sets: sortedSets.length,
          totalSets: sortedSets.length,
          muscleGroups: meta?.muscle_groups || [],
          completedSets: sortedSets,
          notes: exerciseNote,
        };
      });

      return {
        type: 'strength',
        title: workout.title,
        duration: workout.duration_minutes ?? 0,
        completedSets: exercises.reduce((sum, ex) => sum + ex.completedSets.length, 0),
        exercises,
        notes: workoutLevelNote ?? undefined,
      };
    }

    return {
      type: 'custom',
      title: workout.title,
      duration: workout.duration_minutes ?? 0,
      difficulty: workout.workout_type || 'custom',
      caloriesBurned: workout.calories_burned ?? undefined,
      notes: workoutLevelNote ?? undefined,
    };
  };

  const handleSelectWorkout = (workout: Workout) => {
    const formattedWorkout = formatWorkout(workout);
    onWorkoutSelect(formattedWorkout, buildWorkoutAnalysisPayload(workout));
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5 text-primary" />
            {t('components.workoutImport.title')}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('components.workoutImport.loading')}
            </div>
          ) : workouts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('components.workoutImport.empty')}
            </div>
          ) : (
            <div className="space-y-3">
              {workouts.map((workout) => (
                <Card 
                  key={workout.id} 
                  className="cursor-pointer hover:bg-accent/50 transition-colors border-border/50"
                  onClick={() => handleSelectWorkout(workout)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{workout.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(workout.completed_at).toLocaleDateString()}
                          </div>
                          {workout.duration_minutes && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {workout.duration_minutes}{t('components.workoutImport.minSuffix')}
                            </div>
                          )}
                        </div>
                      </div>
                      {workout.workout_type && (
                        <Badge variant="secondary" className="text-xs">
                          {workout.workout_type}
                        </Badge>
                      )}
                    </div>

                    {workout.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {workout.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {workout.exercises.slice(0, 4).map((exercise, index) => (
                        <div key={index} className="flex items-center gap-1 text-xs bg-background/50 px-2 py-1 rounded-md">
                          <Target className="h-3 w-3" />
                          {exercise.name}
                        </div>
                      ))}
                      {workout.exercises.length > 4 && (
                        <div className="text-xs text-muted-foreground px-2 py-1">
                          {t('components.workoutImport.moreExercises', { count: workout.exercises.length - 4 })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};